import { GRID_COLUMNS } from "src/analytics/dashboard/dashboardGridLayout";
import type { Pin, PinLayout } from "src/clients/analytics-dashboard";

export const PIN_DEFAULT_WIDTH = 3;
export const PIN_DEFAULT_HEIGHT = 3;

const occupies = (
  pin: Pin,
  column: number,
  row: number,
  width: number,
  height: number,
): boolean => {
  const { layout } = pin;
  const colOverlap =
    column <= layout.column + layout.width - 1 &&
    column + width - 1 >= layout.column;
  const rowOverlap =
    row <= layout.row + layout.height - 1 && row + height - 1 >= layout.row;
  return colOverlap && rowOverlap;
};

const canPlace = (
  pins: Pin[],
  column: number,
  row: number,
  width: number,
  height: number,
): boolean => !pins.some((pin) => occupies(pin, column, row, width, height));

/**
 * Position a new pin: fill the right slot of the bottom rows first, otherwise
 * start a new row at the left. Mirrors case-blocks' `calculateNextBlockPosition`
 * so two 3-wide pins sit side by side before wrapping.
 */
export const nextPinLayout = (pins: Pin[]): PinLayout => {
  const layout = {
    width: PIN_DEFAULT_WIDTH,
    height: PIN_DEFAULT_HEIGHT,
  };

  if (pins.length === 0) {
    return { column: 1, row: 1, ...layout };
  }

  const maxRowEnd = pins.reduce(
    (max, pin) => Math.max(max, pin.layout.row + pin.layout.height - 1),
    0,
  );
  const earliestRow = Math.max(1, maxRowEnd - PIN_DEFAULT_HEIGHT + 1);
  const rightColumn = GRID_COLUMNS - PIN_DEFAULT_WIDTH + 1;

  const fittingRow = Array.from(
    { length: maxRowEnd - earliestRow + 1 },
    (_, index) => earliestRow + index,
  ).find((row) =>
    canPlace(pins, rightColumn, row, PIN_DEFAULT_WIDTH, PIN_DEFAULT_HEIGHT),
  );

  if (fittingRow !== undefined) {
    return { column: rightColumn, row: fittingRow, ...layout };
  }

  return { column: 1, row: maxRowEnd + 1, ...layout };
};

export const sortPinsByPosition = (pins: Pin[]): Pin[] =>
  pins.toSorted((a, b) =>
    a.layout.row !== b.layout.row
      ? a.layout.row - b.layout.row
      : a.layout.column - b.layout.column,
  );
